import axios from 'axios';

const API_HOST = process.env.REACT_APP_API_HOST;

interface CandidateFilters {
  status?: string;
  category?: string;
  city?: string;
  type?: string;
  dateFrom?: string;
  dateTo?: string;
  name?: string;
}

// Updated to accept filter parameters
export const getCandidates = async (filters?: CandidateFilters) => {
  const response = await axios({
    method: 'get',
    url: `${API_HOST}/v1/ats/candidate`, // Corrected path
    params: filters, // Pass filters as query params
  });
  return response.data;
};

export const getCandidateById = async (id: string) => {
  const response = await axios.get(`${API_HOST}/v1/ats/candidate/${id}`);
  return response.data;
};

export const generateCandidateCvSummary = async (
  id: string,
  options?: { refresh?: boolean },
) => {
  const response = await axios.post(
    `${API_HOST}/v1/ats/candidate/${id}/cv-summary`,
    undefined,
    {
      params: options?.refresh ? { refresh: 'true' } : undefined,
    },
  );
  return response.data;
};

// Removed getCandidatesByStatus as its functionality is covered by getCandidates with filters.

// Added function to create a candidate
// Define a basic interface for the expected data structure
export interface CreateCandidatePayload {
  name: string;
  position?: string;
  department?: string;
  email?: string;
  phone?: string;
  vacancyId?: string;
  coverLetter?: string;
  resume?: {
    title?: string;
    url?: string;
  };
  description?: string;
  location?: string;
  // Add other fields as needed based on your backend DTO
}

export const createCandidate = async (candidateData: CreateCandidatePayload) => {
  const response = await axios.post(`${API_HOST}/v1/ats/candidate`, candidateData);
  return response.data;
};

// ---------------------------------------------------------------------------
// Pipeline
// ---------------------------------------------------------------------------

export const PIPELINE_STAGES = [
  { value: 'applied', label: 'Applied' },
  { value: 'screening', label: 'Screening' },
  { value: 'interview', label: 'Interview' },
  { value: 'offer', label: 'Offer' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number]['value'];

export interface CandidateListParams {
  vacancyId?: string;
  stage?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** A page of candidates, filtered by vacancy, stage and search. */
export const listCandidates = async (
  params: CandidateListParams,
): Promise<{ data: any[]; pagination: Pagination }> => {
  const query: Record<string, string | number> = {
    page: params.page ?? 1,
    limit: params.limit ?? 25,
  };
  if (params.vacancyId) query.vacancyId = params.vacancyId;
  if (params.stage) query.stage = params.stage;
  if (params.search?.trim()) query.search = params.search.trim();

  const response = await axios.get(`${API_HOST}/v1/ats/candidate`, {
    params: query,
  });
  return response.data;
};

export interface CandidateUpdate {
  name?: string;
  email?: string;
  phone?: string;
  position?: string;
  department?: string;
  location?: string;
  description?: string;
  jobId?: string;
}

export const updateCandidate = async (id: string, changes: CandidateUpdate) => {
  const response = await axios.patch(
    `${API_HOST}/v1/ats/candidate/${encodeURIComponent(id)}`,
    changes,
  );
  return response.data;
};

export const moveCandidateStage = async (
  id: string,
  stage: PipelineStage,
  note?: string,
) => {
  const response = await axios.post(
    `${API_HOST}/v1/ats/candidate/${encodeURIComponent(id)}/stage`,
    { stage, ...(note ? { note } : {}) },
  );
  return response.data;
};

export const rejectCandidate = async (
  id: string,
  options: { reason?: string; notifyCandidate?: boolean },
) => {
  const response = await axios.post(
    `${API_HOST}/v1/ats/candidate/${encodeURIComponent(id)}/reject`,
    options,
  );
  return response.data;
};

export const deleteCandidate = async (id: string) => {
  const response = await axios.delete(
    `${API_HOST}/v1/ats/candidate/${encodeURIComponent(id)}`,
  );
  return response.data;
};

/** Emails a candidate (offer, invitation) and optionally moves their stage. */
export const emailCandidate = async (
  id: string,
  message: { subject: string; message: string; stage?: PipelineStage },
) => {
  const response = await axios.post(
    `${API_HOST}/v1/ats/candidate/${encodeURIComponent(id)}/email`,
    message,
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Vacancies
// ---------------------------------------------------------------------------

export interface Vacancy {
  id: string;
  jobTitle: string;
  jobLocation?: string;
  description?: string;
  minimumSalary?: string;
  maximumSalary?: string;
  currency?: string;
  salaryTime?: string;
  jobType?: string;
  workplaceType?: string;
  department?: string;
  visibility?: 'public' | 'private' | string;
  status?: 'open' | 'closed';
  dateCreated?: string;
}

export const getVacancies = async (): Promise<Vacancy[]> => {
  const response = await axios.get(`${API_HOST}/v1/ats/vacancy`);
  return response.data?.data ?? [];
};

export type VacancyUpdate = Partial<
  Pick<
    Vacancy,
    | 'jobTitle'
    | 'jobLocation'
    | 'description'
    | 'minimumSalary'
    | 'maximumSalary'
    | 'currency'
    | 'salaryTime'
    | 'visibility'
    | 'status'
  >
>;

export const updateVacancy = async (id: string, changes: VacancyUpdate) => {
  const response = await axios.patch(
    `${API_HOST}/v1/ats/vacancy/${encodeURIComponent(id)}`,
    { job: changes },
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Dashboard and interviews
// ---------------------------------------------------------------------------

export interface AtsSummary {
  totals: {
    candidates: number;
    openVacancies: number;
    closedVacancies: number;
    upcomingInterviews: number;
    unassignedCandidates: number;
  };
  byStage: { stage: PipelineStage; count: number }[];
  byVacancy: {
    id: string;
    title: string;
    status: 'open' | 'closed';
    visibility?: string;
    total: number;
    byStage: Record<PipelineStage, number>;
  }[];
}

export const getAtsSummary = async (): Promise<AtsSummary> => {
  const response = await axios.get(`${API_HOST}/v1/ats/dashboard/summary`);
  return response.data?.data;
};

export interface InterviewEvent {
  id: string;
  candidate: { id: string; name: string };
  interviewer: { id: string; name: string };
  date: string; // YYYY-MM-DD
  time: string; // HH:mm
  type: string;
  duration: number;
  notes?: string;
}

export const getInterviewEvents = async (
  startDate: string,
  endDate: string,
): Promise<InterviewEvent[]> => {
  const response = await axios.get(`${API_HOST}/v1/ats/calendar/events`, {
    params: { startDate, endDate },
  });
  return response.data?.data ?? [];
};

// ---------------------------------------------------------------------------
// Careers page (jobs.redstonehr.com/<slug>)
// ---------------------------------------------------------------------------

export type CareersPage = {
  slug: string | null;
  url: string | null;
  suggested: string;
};

export const getCareersPage = async (): Promise<CareersPage> => {
  const { data } = await axios.get(`${API_HOST}/v1/ats/careers-page`);
  return data.data;
};

export const setCareersPage = async (slug: string): Promise<CareersPage> => {
  const { data } = await axios.put(`${API_HOST}/v1/ats/careers-page`, { slug });
  return data.data;
};
